/**
 * IrregularVerbQuiz — base form → past simple (or past participle).
 *
 * Frequency-ranked top-50 covers ~87% of irregular verb encounters in
 * naturalistic text (per ESL-Lounge corpus reference).
 *
 * Modes randomized per question:
 *   - "What is the past simple of <base>?"
 *   - "What is the past participle of <base>?"
 *
 * Distractors: past forms drawn from OTHER irregular verbs in the deck.
 * Type-narrows on `card.inflections` — if the card lacks inflections data,
 * isAvailable filters it out at registry-list time.
 */

import { useState, useCallback, useMemo } from 'preact/hooks';
import type { Card } from '@kevvykevwin/lang-core/types';
import type {
  QuizMode,
  QuizProps,
  QuizOptionState
} from '@kevvykevwin/lang-core/types';
import {
  QuizLayout,
  QuizQuestion,
  QuizOptions,
  QuizFeedback,
  QuizOption,
  useKeyboard,
  QUIZ_CONFIG,
  type SrsSignals
} from '@kevvykevwin/lang-core';

import type { EnglishCardExt, VerbExt } from '../types.js';

// ============================================
// Helpers
// ============================================

type Tense = 'past-simple' | 'past-participle';

function pickTense(): Tense {
  return Math.random() < 0.6 ? 'past-simple' : 'past-participle';
}

function getCorrectForm(card: Card<EnglishCardExt> & VerbExt, tense: Tense): string {
  return tense === 'past-simple'
    ? card.inflections.pastSimple
    : card.inflections.pastParticiple;
}

function tenseLabel(tense: Tense): string {
  return tense === 'past-simple' ? 'past simple' : 'past participle';
}

function generateOptions(
  correct: string,
  tense: Tense,
  allVerbs: (Card<EnglishCardExt> & VerbExt)[]
): string[] {
  const distractorPool = new Set<string>();
  for (const card of allVerbs) {
    const form =
      tense === 'past-simple'
        ? card.inflections.pastSimple
        : card.inflections.pastParticiple;
    if (form && form !== correct) distractorPool.add(form);
  }

  const distractors = [...distractorPool].sort(() => Math.random() - 0.5);
  const options = [correct, ...distractors.slice(0, QUIZ_CONFIG.optionsCount - 1)];

  // Pad if pool was small (rare)
  const fallback = ['went', 'gone', 'ate', 'eaten', 'saw', 'seen', 'took', 'taken'];
  while (options.length < QUIZ_CONFIG.optionsCount) {
    const next = fallback.find(f => !options.includes(f));
    if (!next) break;
    options.push(next);
  }
  return options.sort(() => Math.random() - 0.5);
}

function isVerbWithInflections(
  card: Card<EnglishCardExt>
): card is Card<EnglishCardExt> & VerbExt {
  return Boolean(
    card.inflections?.pastSimple && card.inflections.pastParticiple
  );
}

// ============================================
// Inner component
// ============================================

interface InnerProps {
  card: Card<EnglishCardExt> & VerbExt;
  allCards: (Card<EnglishCardExt> & VerbExt)[];
  signals: SrsSignals<EnglishCardExt>;
}

function IrregularVerbQuizInner({ card, allCards, signals }: InnerProps) {
  const answered = signals.quizAnswered.value;

  // Tense + options pinned at first render so re-renders don't reshuffle.
  const [tense] = useState<Tense>(() => pickTense());
  const correct = useMemo(() => getCorrectForm(card, tense), [card, tense]);
  const [options] = useState<string[]>(() => generateOptions(correct, tense, allCards));
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{
    message: string;
    type: 'correct' | 'incorrect';
  } | null>(null);

  const handleAnswer = useCallback(
    (index: number) => {
      if (answered || index >= options.length) return;

      const selected = options[index];
      const isCorrect = selected === correct;

      setSelectedIndex(index);
      signals.quizAnswered.value = true;
      signals.updateWordSRS(card.id, isCorrect);

      if (isCorrect) {
        signals.quizCorrect.value++;
        setFeedback({ message: '✅ Correct!', type: 'correct' });
        signals.showNotification('Correct!', 'success', 1500);
      } else {
        signals.quizIncorrect.value++;
        setFeedback({
          message: `❌ Wrong. ${card.front} → ${correct} (${tenseLabel(tense)})`,
          type: 'incorrect'
        });
        signals.showNotification(
          `${card.front} → ${correct}`,
          'error',
          2500
        );
      }

      setTimeout(() => signals.nextCard(), QUIZ_CONFIG.feedbackDelayMs);
    },
    [answered, card, options, signals, correct, tense]
  );

  useKeyboard({
    currentMode: signals.currentMode,
    quizAnswered: signals.quizAnswered,
    flipCard: signals.flipCard,
    nextCard: signals.nextCard,
    prevCard: signals.prevCard,
    onQuizAnswer: handleAnswer
  });

  const getOptionState = (index: number): QuizOptionState => {
    if (!answered) return 'idle';
    const option = options[index];
    if (option === correct) return 'correct';
    if (index === selectedIndex) return 'incorrect';
    return 'disabled';
  };

  return (
    <QuizLayout modeName="Irregular Verb">
      <QuizQuestion label={`What is the ${tenseLabel(tense)} of:`}>
        <div className="text-5xl font-bold text-gray-800 mb-2">{card.front}</div>
        <div className="text-sm text-slate-500 italic mt-3">{card.back}</div>
      </QuizQuestion>

      <QuizOptions>
        {options.map((option, index) => (
          <QuizOption
            key={`${option}-${index}`}
            text={option}
            state={getOptionState(index)}
            onClick={() => handleAnswer(index)}
            disabled={answered}
            index={index}
          />
        ))}
      </QuizOptions>

      {feedback && <QuizFeedback message={feedback.message} type={feedback.type} />}
    </QuizLayout>
  );
}

// ============================================
// Factory
// ============================================

export interface CreateIrregularVerbQuizOptions {
  signals: SrsSignals<EnglishCardExt>;
  /** Returns all cards; the factory filters to verbs with inflections. */
  getAllCards: () => Card<EnglishCardExt>[];
}

export function createIrregularVerbQuiz(
  options: CreateIrregularVerbQuizOptions
): QuizMode<EnglishCardExt> {
  const { signals, getAllCards } = options;
  return {
    id: 'irregular-verb-quiz',
    label: 'Irregular Verbs',
    icon: '📖',
    isAvailable: deck =>
      deck.cards.some(
        c => c.tags?.includes('irregular-verb') && isVerbWithInflections(c)
      ),
    component: ({ card }: QuizProps<EnglishCardExt>) => {
      if (!isVerbWithInflections(card)) {
        return (
          <div className="card text-center py-12 text-slate-500">
            This card lacks inflection data. Switch decks or pick another mode.
          </div>
        );
      }
      const allVerbs = getAllCards().filter(
        (c): c is Card<EnglishCardExt> & VerbExt => isVerbWithInflections(c)
      );
      return (
        <IrregularVerbQuizInner
          card={card}
          allCards={allVerbs}
          signals={signals}
        />
      );
    }
  };
}
